/**
 * Client-side asset processing for /create-video-new
 * Creates blob URLs and extracts metadata without backend calls
 */
/* eslint-disable no-use-before-define */
import { storeAssetFile, getAssetFile } from './assetFileStorage';

export async function processVideoFile(file) {
  return new Promise((resolve, reject) => {
    const { type, size, name } = file;
    if (!type.startsWith('video/')) {
      reject(new Error('File must be a video'));
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = async () => {
      const { duration, videoWidth: width, videoHeight: height } = video;

      // Generate thumbnail from first frame
      const thumbnailUrl = await generateVideoThumbnail(video, width, height);

      const assetId = `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      
      // Store File object externally (not in Redux state)
      storeAssetFile(assetId, file);
      
      resolve({
        id: assetId,
        type: 'video',
        source_type: 'upload',
        name,
        url: blobUrl,
        thumbnail_url: thumbnailUrl,
        file_size: size,
        mime_type: type,
        duration: Math.round(duration),
        metadata: {
          width,
          height,
          created_at: new Date().toISOString(),
        },
      });
    };

    video.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load video metadata'));
    };

    video.src = blobUrl;
  });
}

export async function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('File is required'));
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[assetProcessing] getVideoDuration start', {
      name: file?.name,
      size: file?.size,
      type: file?.type,
    });
    const blobUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      URL.revokeObjectURL(blobUrl);
    };

    video.onloadedmetadata = () => {
      const duration = Math.round(video.duration || 0);
      // eslint-disable-next-line no-console
      console.log('[assetProcessing] getVideoDuration loadedmetadata', duration);
      cleanup();
      resolve(duration);
    };

    video.onerror = () => {
      // eslint-disable-next-line no-console
      console.log('[assetProcessing] getVideoDuration error');
      cleanup();
      reject(new Error('Failed to load video metadata'));
    };

    video.src = blobUrl;
  });
}

export async function processImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File must be an image'));
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const assetId = `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      
      // Store File object externally (not in Redux state)
      storeAssetFile(assetId, file);
      
      resolve({
        id: assetId,
        type: 'image',
        source_type: 'upload',
        name: file.name,
        url: blobUrl,
        thumbnail_url: blobUrl,
        file_size: file.size,
        mime_type: file.type,
        duration: null,
        metadata: {
          width: img.naturalWidth,
          height: img.naturalHeight,
          created_at: new Date().toISOString(),
        },
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = blobUrl;
  });
}

export async function processDocumentFile(file) {
  const validTypes = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel', // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'text/plain',
    'text/markdown',
  ];
  const validExtensions = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md)$/i;
  if (!validTypes.includes(file.type) && !file.name.match(validExtensions)) {
    throw new Error('File must be a PDF, Office document, or text document');
  }

  const blobUrl = URL.createObjectURL(file);
  const assetId = `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  
  // Store File object externally (not in Redux state)
  storeAssetFile(assetId, file);

  return {
    id: assetId,
    type: 'document',
    source_type: 'upload',
    name: file.name,
    url: blobUrl,
    thumbnail_url: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3C/svg%3E',
    file_size: file.size,
    mime_type: file.type || 'application/octet-stream',
    duration: null,
    metadata: {
      created_at: new Date().toISOString(),
    },
  };
}

export async function processAudioFile(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('audio/')) {
      reject(new Error('File must be an audio file'));
      return;
    }

    const blobUrl = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';

    audio.onloadedmetadata = async () => {
      const duration = Math.round(audio.duration || 0);
      const assetId = `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      
      // Store File object externally (not in Redux state)
      storeAssetFile(assetId, file);
      
      resolve({
        id: assetId,
        type: 'audio',
        source_type: 'upload',
        name: file.name,
        url: blobUrl,
        thumbnail_url: '',
        file_size: file.size,
        mime_type: file.type,
        duration,
        metadata: {
          created_at: new Date().toISOString(),
        },
      });
    };

    audio.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load audio metadata'));
    };

    audio.src = blobUrl;
  });
}

/* eslint-disable no-param-reassign */
async function generateVideoThumbnail(video, width, height) {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const aspectRatio = width / height;
    const thumbWidth = Math.min(400, width);
    const thumbHeight = thumbWidth / aspectRatio;

    canvas.width = thumbWidth;
    canvas.height = thumbHeight;

    const seekTime = Math.min(1, video.duration * 0.1);
    video.currentTime = seekTime;

    video.onseeked = () => {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, thumbWidth, thumbHeight);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(dataUrl);
      } else {
        resolve('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg"%3E%3C/svg%3E');
      }
    };
  });
}
/* eslint-enable no-param-reassign */

export function cleanupBlobUrl(url) {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Recreates blob URL from File object when restoring assets (e.g., during undo/redo)
 * @param {Object} asset - Asset object
 * @returns {Object} - Asset with recreated blob URL
 */
export function recreateBlobUrlIfNeeded(asset) {
  // Only recreate for uploaded assets that have a blob URL
  if (!asset.id || asset.source_type !== 'upload' || !asset.url || !asset.url.startsWith('blob:')) {
    return asset;
  }

  // Get File object from external storage
  const file = getAssetFile(asset.id);
  
  if (!file) {
    // No File object stored, can't recreate
    return asset;
  }

  // Recreate blob URL from File object
  try {
    const newBlobUrl = URL.createObjectURL(file);
    
    // For images, thumbnail_url might be the same as url
    const newThumbnailUrl = asset.type === 'image' && asset.thumbnail_url === asset.url
      ? newBlobUrl
      : asset.thumbnail_url;

    return {
      ...asset,
      url: newBlobUrl,
      thumbnail_url: newThumbnailUrl,
    };
  } catch (error) {
    // If we can't recreate, return asset as-is
    console.warn('Failed to recreate blob URL for asset:', asset.id, error);
    return asset;
  }
}
